// FormsList.js
import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Typography,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import AddIcon from '@mui/icons-material/Add';
import FormBuilder from '../AddForms/FormBuilder';
import { APIApplicationFormArchive, APIApplicationFormList, APIApplicationFormPublish } from '../../utilities/APIs';
import CachedIcon from '@mui/icons-material/Cached';
import FormViewer from './FormViewer';
import { getLoggedInUserName } from '../../utilities/Functions';
import FormEditBuilder from '../EditForms/FormEditBuilder';
import { TEXT_ARCHIVE, TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import ApplicationsViewListing from '../ApplicationForm/ApplicationsViewListing';
import { useAuth } from '../../users/AuthContext';
import useCustomToast from '../../hooks/useToast';
import FormPrint from './FormPrint';
import { useNavigate } from 'react-router-dom';

export default function FormsList({ initialView = "LISTING" }) {
    const showToast  = useCustomToast();
    const { makeAuthenticatedRequest } = useAuth();
    const [forms, setForms] = useState([]);
    const [currentView, setCurrentView] = useState(initialView);
    const [selectedForm, setSelectedForm] = useState(null);
    const [loading, setLoading] = useState(true);    
    const [error, setError] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [formToDelete, setFormToDelete] = useState(null);    
    const [publishingForm, setPublishingForm] = useState(null);
    const [selectedApplicationType, setSelectedApplicationType] = useState(null);
    const navigate = useNavigate();

    const fetchForms = async () => {
        setLoading(true);
        setError(null);          try {
            const data = await makeAuthenticatedRequest(APIApplicationFormList);
            let forms = data.data ? data.data.reverse() : [];
            setForms(forms);
            // showSuccess('Forms loaded successfully.');
            showToast('Data Fetching', 'Data Fetched  Successfully', 'success');
        } catch (err) {
            // setError(err.message || 'Failed to fetch forms');
            showToast("Data Fetching Error",err.message || 'Failed to load forms.',"error")
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchForms();
    }, []);

    const handleViewClick = (form) => {
        setSelectedForm(form);
        sessionStorage.setItem('viewForm',JSON.stringify(form))
        navigate('/application/form/view');
    };



    const handleEditClick = (form) => {
        // Parse the formConfigJson if it's a string
        const formData = typeof form.formConfigJson === 'string' 
            ? JSON.parse(form.formConfigJson) 
            : form.formConfigJson;
        setSelectedForm({ ...form, ...formData });
        setCurrentView("EDITING");
    };

    const handleDeleteClick = (form) => {
        setFormToDelete(form);
        setDeleteConfirmOpen(true);
    };    

    const handlePrintClick = (form) => {
        setSelectedForm(form);
        setCurrentView("PRINTING");
        // setSelectedForm(form); 
        // setPrintView(true);
    };

    const confirmDelete = async () => {
        try {
            let createdBy = getLoggedInUserName();
            const formData = new FormData();
            if (createdBy && createdBy !== "DefaultUser") {
                formData.append('createdBy', createdBy);
            }
            formData.append('deleteId', formToDelete.id);

            const response = await makeAuthenticatedRequest(APIApplicationFormArchive, {
                method: 'DELETE',
                body: formData
            });

            if (response.status === "success") {
                showToast(
                    'Success!',
                    response.message || "Form archived successfully!",
                    'success'
                );
                setForms(forms.filter(form => form.id !== formToDelete.id));
                setDeleteConfirmOpen(false);
                setFormToDelete(null);
            } else {
                showToast(
                    'Warning!',
                    response.message || "Form archiving failed",
                    'warning'
                );
            }
        } catch (error) {
            console.log("Error archiving form:", error);
            showToast(
                'Error!',
                error.message.includes("Network") 
                    ? "Network error: " + TEXT_CONNECTION_ERROR
                    : "An error occurred while archiving the form: " + error.message,
                'error'
            );
        }
    };  
        
        const handlePublishToggle = async (form) => {
            setPublishingForm(form.id);
            const newPublishStatus = !form.isPublished;
            
            try {
                const params = new URLSearchParams();
                const createdBy = getLoggedInUserName();
                
                if (createdBy && createdBy !== "DefaultUser") {
                    params.append('createdBy', createdBy);
                }
                params.append('formId', form.id);
                params.append('isPublished', newPublishStatus);            
                const response = await makeAuthenticatedRequest(
                    `${APIApplicationFormPublish}?${params.toString()}`,
                    {
                        method: 'PUT'
                    }
                );

                if (response && response.status === "SUCCESS") {
                    setForms(forms.map(f => 
                        f.id === form.id 
                            ? { ...f, isPublished: newPublishStatus }
                            : f
                    ));
                    
                    const action = newPublishStatus ? 'published' : 'unpublished';
                    showToast(
                        'Success!',
                        `Form ${action} successfully!`,
                        'success'
                    );
                } else {
                    showToast(
                        'Warning!',
                        response?.message || "Failed to update publish status",
                        'warning'
                    );
                }
            } catch (error) {
                console.error("Error updating publish status:", error);
                showToast(
                    'Error!',
                    error.status === 401 || error.status === 403
                        ? "Authentication error: You must be logged in to change form status."
                        : error.message?.includes("Network")
                            ? "Network error: " + TEXT_CONNECTION_ERROR
                            : "An error occurred while updating form status: " + error.message,
                    'error'
                );
            } finally {
                setPublishingForm(null);
            }
        };    const handleAddNew = () => {
        setSelectedForm(null);
        setCurrentView("ADDING");
    };

    const handleSelectApplicationType = (type, title) => {
        setSelectedApplicationType({ type, title });
        setCurrentView("APPLICATIONS");
    };


    const handleBackToList = () => {
        setCurrentView("LISTING");
        setSelectedForm(null);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box p={3} sx={{ overflow: 'auto', height: 'calc(100vh - YOUR_HEADER_HEIGHT_HERE)' }}> 
            {currentView === "LISTING" && (
                <>                    
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Button 
                            variant="contained" 
                            color='secondary'
                            startIcon={<CachedIcon />}
                            onClick={fetchForms}
                        >
                            Reload Forms
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />}
                            onClick={handleAddNew}
                        >
                            Add New Form
                        </Button>
                    </Box>
                    <Typography variant="h4">Forms List</Typography>
                    <TableContainer component={Paper}>
                        <Table>                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Category Type</b></TableCell>
                                    {/* <TableCell><b>Form Name</b></TableCell> */}
                                    <TableCell><b>Status</b></TableCell>
                                    <TableCell><b>Created At</b></TableCell>
                                    <TableCell><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {forms.map((form) => {
                                    const formData = typeof form.formConfigJson === 'string' 
                                        ? JSON.parse(form.formConfigJson) 
                                        : form.formConfigJson;
                                    return (                                        
                                        <TableRow key={form.id}>
                                            {/* Category Type Cell with Tooltip */}
                                            <TableCell>
                                                <Tooltip 
                                                    title={formData.name} 
                                                    arrow 
                                                    placement="top"
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'success.main',
                                                                color: 'common.white',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'success.main',
                                                                },
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <span>{formData.type}</span>
                                                </Tooltip>
                                            </TableCell>
                                            
                                            {/* Status Cell with Tooltip */}
                                            <TableCell>
                                                <Tooltip 
                                                    title={formData.name} 
                                                    arrow 
                                                    placement="top"
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'success.main',
                                                                color: 'common.white',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'success.main',
                                                                },
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center">
                                                        {form.isPublished === true ? (
                                                            <>
                                                                <PublishIcon color="success" sx={{ mr: 1 }} />
                                                                <Typography color="success.main">Published</Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <UnpublishedIcon color="warning" sx={{ mr: 1 }} />
                                                                <Typography color="warning.main">Draft</Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            
                                            {/* Created At Cell with Tooltip */}
                                            <TableCell>
                                                <Tooltip 
                                                    title={formData.name} 
                                                    arrow 
                                                    placement="top"
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'success.main',
                                                                color: 'common.white',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'success.main',
                                                                },
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <span>{new Date(form.createdAt).toLocaleDateString()}</span>
                                                </Tooltip>
                                            </TableCell>
                                            
                                            {/* Actions Cell (no tooltip) */}
                                            <TableCell>
                                                <Button
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => handleViewClick(form)}
                                                    sx={{ mr: 1 }}
                                                    size="small"
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    startIcon={<EditIcon />}
                                                    onClick={() => handleEditClick(form)}
                                                    sx={{ mr: 1 }}
                                                    color="secondary"
                                                    size="small"
                                                >
                                                    Edit
                                                </Button>                                                
                                                <Button
                                                    startIcon={form.isPublished === true ? <UnpublishedIcon /> : <PublishIcon />}
                                                    onClick={() => handlePublishToggle(form)}
                                                    sx={{ mr: 1 }}
                                                    color={form.isPublished === true ? "warning" : "success"}
                                                    disabled={publishingForm === form.id}
                                                    size="small"
                                                >
                                                    {publishingForm === form.id 
                                                        ? "..." 
                                                        : form.isPublished === true ? "Unpublish" : "Publish"
                                                    }
                                                </Button>
                                                <Button
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => handleDeleteClick(form)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    {TEXT_ARCHIVE}
                                                </Button>
                                                <Button
                                                    startIcon={<PrintIcon />}
                                                    onClick={() => handlePrintClick(form)}
                                                    sx={{ mr: 1 }}
                                                    color="info"
                                                    size="small"
                                                >
                                                    Print
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}            
            {currentView === "APPLICATION_TYPES" && (
                <Box p={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h4">Select Application Type to View</Typography>
                        <Button 
                            variant="contained" 
                            color='error'
                            onClick={handleBackToList}
                        >
                            Back to Forms
                        </Button>
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Button 
                            variant="outlined" 
                            size="large"
                            onClick={() => handleSelectApplicationType("UNDER", "Undergraduate Applications")}
                            sx={{ minWidth: 200, minHeight: 60 }}
                        >
                            Undergraduate Applications
                        </Button>
                        <Button 
                            variant="outlined" 
                            size="large"
                            onClick={() => handleSelectApplicationType("GRADUATE", "Graduate Applications")}
                            sx={{ minWidth: 200, minHeight: 60 }}
                        >
                            Graduate Applications
                        </Button>                        <Button 
                            variant="outlined" 
                            size="large"
                            onClick={() => handleSelectApplicationType("SHORT", "Short Course Applications")}
                            sx={{ minWidth: 200, minHeight: 60 }}
                        >
                            Short Course Applications
                        </Button>
                    </Box>
                </Box>
            )}
            {currentView === "APPLICATIONS"&&(
                <ApplicationsViewListing  
                    applicationViewType={selectedApplicationType?.type}
                    onCancel={handleBackToList}
                    titleViewName={selectedApplicationType?.title}
                />
            )}

            {currentView === "EDITING" && (
                <FormEditBuilder 
                    formData={selectedForm}
                    onCancel={handleBackToList}
                />
            )}

            {currentView === "ADDING" && (
                <FormBuilder onCancel={handleBackToList}/>
            )}

            {currentView === "PRINTING" && (
                <FormPrint 
                    formData={selectedForm} 
                    onClose={handleBackToList}
                />
            )}

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to Archive this form ??
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error">{TEXT_ARCHIVE}</Button>
                </DialogActions>
            </Dialog>
        </Box>

        
    );
}